export type MediaType = "ANIME" | "MANGA";
export type CraftLevel = "simple" | "intermediate" | "complex";
export type MobilityLevel = "low" | "mixed" | "high";
export type ExposureLevel = "covered" | "flexible" | "open";
export type Silhouette = "casual" | "uniform" | "dress" | "armor" | "any";

export interface CatalogCharacter {
  id: string;
  name: string;
  nativeName?: string;
  siteUrl: string;
  image?: string;
  role: "MAIN" | "SUPPORTING" | "BACKGROUND";
}

export interface CatalogMedia {
  id: string;
  type: MediaType;
  title: string;
  nativeTitle?: string;
  siteUrl: string;
  coverImage?: string;
  color?: string;
  description?: string;
  genres: string[];
  tags: string[];
  averageScore?: number;
  popularity?: number;
  seasonYear?: number;
  characters: CatalogCharacter[];
  source: "AniList live" | "해볼캐 seed";
  sourceCheckedAt: string;
}

export interface CosplayPreferences {
  mediaType: MediaType;
  genres: string[];
  silhouette: Silhouette;
  craft: CraftLevel;
  mobility: MobilityLevel;
  exposure: ExposureLevel;
  budgetKrw: number;
}

export interface CosplayCandidate {
  id: string;
  media: CatalogMedia;
  character: CatalogCharacter;
  tasteMatchCount: number;
  reasons: string[];
  tradeoffs: string[];
}

export type ProjectStage = "discover" | "prepare" | "onsite" | "photos" | "archive";
export type TaskStatus = "todo" | "doing" | "done";

export interface ProjectTask {
  id: string;
  stage: ProjectStage;
  label: string;
  status: TaskStatus;
  dueLabel: string;
  custom?: boolean;
  dueDate?: string;
}

export interface CosplayProject {
  id: string;
  title: string;
  mediaTitle: string;
  characterName: string;
  characterSourceUrl: string;
  createdAt: string;
  dDay: string;
  preferences: CosplayPreferences;
  tasks: ProjectTask[];
  note: string;
  budgetLimitKrw?: number;
  eventId?: string;
  sourceProjectId?: string;
  bag?: { id: string; label: string; done: boolean }[];
}

export type WardrobeStatus = "unselected" | "owned" | "borrow" | "make" | "buy";

export interface WardrobeItem {
  id: string;
  projectId?: string;
  category: "costume" | "wig" | "prop" | "makeup" | "shoes" | "doujin";
  name: string;
  status: WardrobeStatus;
  budgetKrw: number;
  ready: boolean;
  measurementNote: string;
  dueDate: string;
  links?: string[];
  shop?: string;
  reuseEstimate?: { sourceItemId: string; plannedKrw: number; capturedAt: string };
  quoteStatus: "none" | "requested" | "received" | "cancelled";
}

export interface MarketDraft {
  id: string;
  projectId?: string;
  kind: "used-costume" | "commission" | "all-ages-doujin";
  title: string;
  priceKrw: number;
  sizeOrScope: string;
  condition: string;
  createdAt: string;
  status: "draft" | "ready" | "cancelled";
}

export interface ShootMember {
  id: string;
  name: string;
  looks: string[];
  unavailableSlots: number[];
  unavailable?: { id?: string; from: string; to: string }[];
}

export interface ShootSlot {
  kind?: "shoot" | "break";
  minutes?: number;
  index: number;
  time: string;
  memberName: string;
  look: string;
  photographer: string;
}

export interface ShootPlan {
  id: string;
  projectId?: string;
  title: string;
  startTime: string;
  slotMinutes: number;
  members: ShootMember[];
  photographers: string[];
  slots: ShootSlot[];
  generatedAt: string;
  breakEvery?: number;
  breakMinutes?: number;
  dayItems?: { id: string; time: string; minutes: number; label: string; kind: "stage" | "change" | "meal" | "move" | "other" }[];
}

export interface RecruitmentDraft {
  id: string;
  projectId?: string;
  organizerAge: number;
  participantAge: number;
  venue: "public" | "private";
  guardianPresent: boolean;
  status: "draft";
  createdAt: string;
}

export interface BookingDraft {
  id: string;
  projectId?: string;
  photographerName: string;
  shots?: number;
  requesterMinor?: boolean;
  venue?: "public" | "studio" | "private";
  priceKrw: number;
  cancellation: string;
  deliveryDate: string;
  rightsScope: string;
  status: "draft" | "requested" | "cancelled";
  createdAt: string;
}

export interface SafetyAction {
  id: string;
  kind: "block" | "report";
  targetAlias: string;
  reason: string;
  status: "local" | "draft";
  createdAt: string;
}

export type ConsentScope = "social" | "portfolio" | "commercial";

export interface PhotoRecord {
  id: string;
  projectId?: string;
  fileName: string;
  photographer: string;
  retouchStatus: "raw" | "requested" | "approved";
  consent: Record<ConsentScope, boolean>;
  receivedAt: string;
  consentRequestId?: string;
  appliedConsentRequestId?: string;
  consentHistory?: { at: string; scope: ConsentScope; allowed: boolean; via: "self" | "reply" }[];
}

export interface ChallengeDraft {
  id: string;
  projectId: string;
  title: string;
  photoIds: string[];
  status: "draft";
  createdAt: string;
}

export type EntitlementStatus = "free" | "active" | "grace" | "refunded" | "revoked" | "expired";

export interface EntitlementState {
  offerId?: string;
  status: EntitlementStatus;
  source: "none" | "web_pg" | "toss_iap";
  transactionId?: string;
  expiresAt?: string;
  updatedAt: string;
}

export type AnalyticsEventName =
  | "landing_view"
  | "recommendation_viewed"
  | "character_selected"
  | "project_created"
  | "project_stage_completed"
  | "listing_draft_saved"
  | "schedule_generated"
  | "consent_updated"
  | "portfolio_opened"
  | "return_opened"
  | "offer_viewed";

export interface AnalyticsEvent {
  id: string;
  name: AnalyticsEventName;
  occurredAt: string;
  properties: Record<string, string | number | boolean>;
}

export interface AnalyticsState {
  consent: boolean;
  queue: AnalyticsEvent[];
  attribution?: { source?: string; medium?: string; campaign?: string };
}

export interface AppData {
  version: 3;
  lastBackupAt?: string;
  activeProjectId?: string;
  projects: CosplayProject[];
  wardrobe: WardrobeItem[];
  marketDrafts: MarketDraft[];
  shootPlans: ShootPlan[];
  recruitmentDrafts: RecruitmentDraft[];
  bookingDrafts: BookingDraft[];
  safetyActions: SafetyAction[];
  photos: PhotoRecord[];
  challengeDrafts: ChallengeDraft[];
  entitlement: EntitlementState;
  analytics: AnalyticsState;
  lastPreferences: CosplayPreferences;
}

export const defaultPreferences: CosplayPreferences = {
  mediaType: "ANIME",
  genres: ["Fantasy"],
  silhouette: "any",
  craft: "intermediate",
  mobility: "mixed",
  exposure: "covered",
  budgetKrw: 200_000,
};

export const emptyAppData: AppData = {
  version: 3,
  activeProjectId: undefined,
  projects: [],
  wardrobe: [],
  marketDrafts: [],
  shootPlans: [],
  recruitmentDrafts: [],
  bookingDrafts: [],
  safetyActions: [],
  photos: [],
  challengeDrafts: [],
  entitlement: { status: "free", source: "none", updatedAt: "2026-08-23T00:00:00.000Z" },
  analytics: { consent: false, queue: [] },
  lastPreferences: defaultPreferences,
};
